import React from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown } from 'lucide-react';

import { ORGANIZATION_TYPES, type OrganizationType } from '../../types';
import { UI_Z_INDEX } from '../../utils/uiZIndex';

type Variant = 'default' | 'detail';

const variantClasses: Record<
  Variant,
  { trigger: string; option: string; triggerPad: string; optionPad: string }
> = {
  default: {
    trigger: 'text-sm font-medium text-app-text',
    option: 'text-sm font-medium',
    triggerPad: 'px-3 py-2',
    optionPad: 'px-3 py-2.5',
  },
  detail: {
    trigger: 'text-sm font-medium text-app-text',
    option: 'text-sm font-medium',
    triggerPad: 'px-2 py-2',
    optionPad: 'px-2.5 py-2.5',
  },
};

/** Collapsed dropdown for organisation type (no native `<select>`). */
export const OrganizationTypePicker: React.FC<{
  value: OrganizationType | null;
  onChange: (t: OrganizationType) => void;
  /** `detail` matches Organisation-Detail EditDataRow controls; `default` matches CreateOrganizationModal. */
  variant?: Variant;
}> = ({ value, onChange, variant = 'default' }) => {
  const [open, setOpen] = React.useState(false);
  const [menuPos, setMenuPos] = React.useState<{
    top: number;
    left: number;
    width: number;
    maxHeight: number;
  } | null>(null);
  const wrapRef = React.useRef<HTMLDivElement>(null);
  const triggerRef = React.useRef<HTMLDivElement>(null);
  const panelRef = React.useRef<HTMLDivElement>(null);

  const vc = variantClasses[variant];
  const display = value ?? '—';

  const updateMenuPosition = React.useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const margin = 8;
    const maxPanel = 220;
    const spaceBelow = window.innerHeight - rect.bottom - margin;
    const spaceAbove = rect.top - margin;
    const openUp = spaceBelow < 120 && spaceAbove > spaceBelow;
    const available = openUp ? spaceAbove : spaceBelow;
    let left = rect.left;
    const width = rect.width;
    if (left + width > window.innerWidth - margin) {
      left = Math.max(margin, window.innerWidth - margin - width);
    }
    if (left < margin) left = margin;
    const maxHeight = Math.min(maxPanel, Math.max(120, available));
    setMenuPos({
      top: openUp ? Math.max(margin, rect.top - 4 - maxHeight) : rect.bottom + 4,
      left,
      width,
      maxHeight,
    });
  }, []);

  React.useLayoutEffect(() => {
    if (!open) {
      setMenuPos(null);
      return;
    }
    updateMenuPosition();
    const onScroll = (e: Event) => {
      const t = e.target as Node | null;
      if (!t) return;
      if (panelRef.current?.contains(t)) return;
      updateMenuPosition();
    };
    window.addEventListener('resize', updateMenuPosition);
    document.addEventListener('scroll', onScroll, true);
    return () => {
      window.removeEventListener('resize', updateMenuPosition);
      document.removeEventListener('scroll', onScroll, true);
    };
  }, [open, updateMenuPosition]);

  React.useEffect(() => {
    if (!open) return;
    const onOutside = (e: MouseEvent) => {
      const t = e.target as Node;
      if (wrapRef.current?.contains(t)) return;
      if (panelRef.current?.contains(t)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      e.stopPropagation();
      setOpen(false);
    };
    document.addEventListener('mousedown', onOutside);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onOutside);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div className="relative min-w-0" ref={wrapRef}>
      <div ref={triggerRef}>
        <button
          type="button"
          aria-expanded={open}
          aria-haspopup="listbox"
          onClick={() => setOpen((o) => !o)}
          className={`w-full flex items-center justify-between gap-2 ${vc.triggerPad} text-left ${vc.trigger} bg-app-surface border border-app-border rounded focus:ring-1 focus:ring-pipedrive-blue outline-none`}
        >
          <span className={`truncate ${value == null ? 'text-app-text-subtle' : ''}`}>{display}</span>
          <ChevronDown
            className={`w-4 h-4 shrink-0 text-app-text-subtle transition-transform ${open ? 'rotate-180' : ''}`}
          />
        </button>
      </div>
      {open &&
        menuPos &&
        createPortal(
          <div
            ref={panelRef}
            role="listbox"
            data-org-type-picker-panel
            style={{
              position: 'fixed',
              top: menuPos.top,
              left: menuPos.left,
              width: menuPos.width,
              maxHeight: menuPos.maxHeight,
              zIndex: UI_Z_INDEX.portalDropdownInModal,
            }}
            className="bg-app-surface border border-app-border rounded-lg shadow-lg overflow-hidden flex flex-col min-h-0 overscroll-y-contain py-1"
            onWheel={(e) => e.stopPropagation()}
          >
            {ORGANIZATION_TYPES.map((t) => {
              const active = value === t;
              return (
                <button
                  key={t}
                  type="button"
                  role="option"
                  aria-selected={active}
                  onClick={() => {
                    onChange(t);
                    setOpen(false);
                  }}
                  className={`w-full text-left ${vc.optionPad} ${vc.option} transition-colors ${
                    active ? 'bg-pipedrive-blue text-white' : 'text-app-text hover:bg-app-hover'
                  }`}
                >
                  {t}
                </button>
              );
            })}
          </div>,
          document.body,
        )}
    </div>
  );
};
